import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Shield, Star, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, { createCheckoutSession } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

const Pricing = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const plans = [
    {
      id: 'pack5',
      nameKey: 'pricing.starterName',
      price: '$5',
      periodKey: 'pricing.oneTime',
      highlight: false,
      descriptionKey: 'pricing.starterDescription',
      features: [
        'Use AI up to 5 times to choose the best template for your profile and target job',
        '5 detailed AI resume reviews with score and improvement suggestions',
        'Works with resumes built in the app or PDF/DOCX/TXT files you upload',
        'One-time payment – credits never expire',
      ],
    },
    {
      id: 'pack10',
      nameKey: 'pricing.proName',
      price: '$10',
      periodKey: 'pricing.oneTime',
      highlight: true,
      descriptionKey: 'pricing.proDescription',
      features: [
        'Everything in Starter, but with 10 AI template picks for different roles or versions',
        '10 detailed AI resume reviews with score and improvement suggestions',
        'Ideal if you are applying to many positions or tailoring multiple versions',
        'One-time payment – credits never expire',
      ],
    },
    {
      id: 'enterprise',
      nameKey: 'pricing.enterpriseName',
      price: '$20',
      periodKey: 'pricing.perMonth',
      highlight: false,
      descriptionKey: 'pricing.enterpriseDescription',
      features: [
        'Unlimited AI template recommendations',
        'Unlimited AI resume analyses',
        'All professional templates',
        'Unlimited resume builder usage',
      ],
    },
  ];

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: '/pricing' } });
      return;
    }

    try {
      const successUrl = `${window.location.origin}/pricing?status=success&plan=${planId}`;
      const cancelUrl = `${window.location.origin}/pricing?status=cancelled`;

      const response = await api.post('/api/billing/create-checkout-session/', {
        plan: planId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.error || 'Failed to start checkout.');
    }
  };

  const isPremium = user?.is_premium;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
          {isPremium && (
            <p className="mt-3 text-sm text-green-700 bg-green-100 inline-flex items-center px-3 py-1 rounded-full">
              <Shield className="w-4 h-4 mr-1" />
              {t('pricing.alreadyPremium')} ({user.subscription_plan} plan).
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-2xl border shadow-sm bg-white p-8 flex flex-col ${
                plan.highlight ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
              }`}
            >
              {plan.highlight && (
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4 self-start">
                  <Star className="w-4 h-4 mr-1" />
                  {t('pricing.mostPopular')}
                </div>
              )}

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t(plan.nameKey)}
              </h2>
              <p className="text-gray-600 mb-4">
                {t(plan.descriptionKey)}
              </p>

              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-gray-900 mr-1">{plan.price}</span>
                <span className="text-gray-500 text-sm">
                  {t(plan.periodKey)}
                </span>
              </div>

              <ul className="space-y-2 mb-8 flex-1">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <Check className="w-4 h-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.id)}
                className={`w-full inline-flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  plan.highlight
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {plan.id === 'enterprise'
                  ? isPremium
                    ? t('pricing.manageSubscription')
                    : t('pricing.startMonthly')
                  : t('pricing.buyPack')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>

              <p className="mt-3 text-xs text-gray-500 text-center">
                Cancel anytime. Secure payments powered by Stripe.
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-2xl shadow-sm p-6 flex items-start gap-4">
          <div className="p-2 rounded-full bg-primary-100 text-primary-700">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t('pricing.footerTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              {t('pricing.footerP1')}
            </p>
            <p className="text-sm text-gray-600">
              {t('pricing.footerP2')}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;


